import * as React from 'react'
import { useSelector } from 'react-redux'
import { GridDensity, GridState } from '@mui/x-data-grid-pro'
import { useAppDispatch } from '@user/store/hooks'
import { WWMXGrid } from '@user/components/xGrid'
import {
  defaultOpportunityColumns,
  lockedOpportunityColumns,
} from '@user/components/Opportunities/OpportinutyListView/opportunityListColumns'
import {
  IUserPreferenceGeneralListItemType,
  saveGeneralPreferences,
  userGeneralPreferencesList,
} from '@user/store/userPreferences/userPreferencesGeneral'
import { useGuestUserOppsColumns } from './hooks/Opportunity/useGuestUserOppsColumns'
import { useGuestUserOppsActions } from './hooks/Opportunity/useGuestUserOppsActions'
import {
  getDefaultGuestUserOppsColumnVisibility,
  selectGuestUserOpportunities,
  selectGuestUserOpportunitiesLoading,
  selectGuestUserOpportunitiesTotalCount,
  selectGuestUserOppsFilterSet,
  selectGuestUserOppsPage,
  selectGuestUserOppsPerPage,
} from '@user/store/guestUser/slice'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import SearchIcon from '@mui/icons-material/Search'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import {
  defaultGuestUserOpportunityFilters,
  guestUserClearedOpportunityFilters,
} from '@user/store/guestUser/filters'
import { useSaveOppsFilters } from '@user/components/Dashboard/GuestUserDashboard/hooks/Opportunity/useSaveGuestUserOppsFilters'
import { orderByOptions } from '@user/store/opportunity'
import { validateOppFilters } from '@user/components/Opportunities/OpportunityFilters/utils'
import { OpportunityFilters } from '@user/components/Opportunities/OpportunityFilters'
import { Deposit } from '@workwave-tidal/core/illustrations/Deposit'
import Typography from '@mui/material/Typography'

interface GuestUserOppsTableProps {
  setFiltersDisabled: (state: boolean) => void
  coreDataLoading?: boolean
}

export function GuestUserOppsTable(props: GuestUserOppsTableProps) {
  const dispatch = useAppDispatch()
  const { columns, columnVisibility } = useGuestUserOppsColumns()

  const opportunities = useSelector(selectGuestUserOpportunities)
  const loading = useSelector(selectGuestUserOpportunitiesLoading)
  const count = useSelector(selectGuestUserOpportunitiesTotalCount)
  const page = useSelector(selectGuestUserOppsPage)
  const perPage = useSelector(selectGuestUserOppsPerPage)
  const filterSet = useSelector(selectGuestUserOppsFilterSet)
  const saveOppsFilters = useSaveOppsFilters()
  const defaultVisibility = getDefaultGuestUserOppsColumnVisibility()

  const {
    handlePaginationModelChange,
    sortModel,
    onSortModelChange,
    onSaveColumnState,
    selectionChangeCallback,
    onColumnWidthChange,
    handleSearchChange,
    searchText,
    onClickExport,
  } = useGuestUserOppsActions({ ...props, columns })

  const userGeneralPreferences = useSelector(userGeneralPreferencesList)

  const densityModeUserGeneralPreference = userGeneralPreferences?.find(
    (row) =>
      row?.type ==
      IUserPreferenceGeneralListItemType.GuestUserDashboardOpportunitiesDensityMode,
  )

  const stringValue = densityModeUserGeneralPreference?.value ?? 'standard'
  const densityMode = stringValue as GridDensity

  const onStateChange = (state: GridState) => {
    if (state.density) {
      onDensityChange(state.density.value)
    }
  }

  const onDensityChange = (density: string) => {
    if (densityMode != density) {
      dispatch(
        saveGeneralPreferences({
          type: IUserPreferenceGeneralListItemType.GuestUserDashboardOpportunitiesDensityMode,
          value: density,
        }),
      )
    }
  }

  return (
    <Box position="relative">
      <TextField
        sx={{ position: 'absolute', right: '20px', zIndex: 1000 }}
        onChange={handleSearchChange}
        value={searchText}
        placeholder="Search..."
        spellCheck={false}
        variant="standard"
        type="search"
        InputProps={{
          sx: {
            padding: (theme) => `${theme.spacing(0.9)} ${theme.spacing(1.2)}`,
            '&::after': { borderBottom: '1px solid #' },
            '&::before': { borderBottom: '1px solid #ffffff00' },
            borderBottom: 'none',
          },
          startAdornment: (
            <InputAdornment position="start">
              <IconButton size="small">
                <SearchIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <WWMXGrid
        loading={loading || props.coreDataLoading}
        applyCustomStyles={true}
        virtualScrollerHeight={
          densityMode === 'compact'
            ? 325
            : densityMode === 'standard'
              ? 342
              : 358
        }
        // columns
        columnVisibilityModel={columnVisibility}
        columns={columns}
        onColumnWidthChange={(e) =>
          onColumnWidthChange(e?.colDef, columns, columnVisibility)
        }
        columnsSelectorProps={{
          defaultVisibility: defaultVisibility,
          defaultColumns: defaultOpportunityColumns,
          lockedColumns: lockedOpportunityColumns,
          saveCallback: (e) => onSaveColumnState(e, columns),
        }}
        //state
        isDensityEnable
        isExportEnable
        density={densityMode}
        onStateChange={onStateChange}
        //export
        exportClickCallback={onClickExport}
        exportFileName="Opportunities"
        // rows
        rows={opportunities}
        rowCount={count}
        // pagination
        paginationModel={{ page: page - 1, pageSize: perPage }}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={[10, 25, 50]}
        // sorting
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        selectionChangeCallback={selectionChangeCallback}
        width="100%"
        isFilterEnable
        gridFilterProps={{
          applyCallback: saveOppsFilters,
          clearedValues: guestUserClearedOpportunityFilters,
          defaultValues: defaultGuestUserOpportunityFilters,
          initialValues: filterSet,
          orderOptions: orderByOptions,
          hideOrderBy: true,
          hideCreatedBy: true,
          disabled: false,
          validate: validateOppFilters,
          filterComponent: OpportunityFilters,
          appliedCount: filterSet?.appliedCount || 0,
        }}
        slots={{
          noRowsOverlay: () => (
            <Box
              style={{ backgroundColor: 'white', border: '1px solid #e0e0e0' }}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="100%"
              textAlign="center"
              width={`calc(100vw - 87px)`}
              zIndex={1000}
            >
              <Deposit style={{ fontSize: '100px' }} />
              <Typography variant="h6" color="textSecondary" mt={2} mb={2}>
                No opportunities to display
              </Typography>
            </Box>
          ),
        }}
      />
    </Box>
  )
}

GuestUserOppsTable.displayName = 'GuestUserOppsTable'
